import type { InternalAxiosRequestConfig, AxiosError } from 'axios'

import { useAuthStore } from '@stores/auth'

import axios from 'axios'
import routes from '@config/routes'
import { AUTH_STORAGE_KEYS } from '@auth/storage'
import { buildLoginLocation } from '@auth/login-redirect'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()

  const api = axios.create({
    baseURL: config.public.appUrl,
    withCredentials: false,
    timeout: 60000,
  })

  const auth = useAuthStore()
  if (!auth.accessToken) auth._hydrateFromStorage()

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key && !AUTH_STORAGE_KEYS.includes(event.key)) return
    auth._hydrateFromStorage()
  })

  api.interceptors.request.use((req: InternalAxiosRequestConfig & { _retry?: boolean, _authMode?: 'none' | 'access' | 'refresh' }) => {
    req.headers = req.headers || {}

    if (req._authMode === 'none') return req

    if (req._authMode === 'refresh') {
      if (auth.refreshToken) req.headers.Authorization = `${auth.tokenType} ${auth.refreshToken}`
      return req
    }

    if (req._authMode === 'access') {
      if (auth.accessToken) req.headers.Authorization = `${auth.tokenType} ${auth.accessToken}`
      return req
    }

    const url = (req.url || '').toString()
    const isRefreshCall = url.includes(routes['admin.auth.refresh'])

    if (isRefreshCall) {
      if (auth.refreshToken) req.headers.Authorization = `${auth.tokenType} ${auth.refreshToken}`
    } else if (auth.accessToken) {
      req.headers.Authorization = `${auth.tokenType} ${auth.accessToken}`
    }

    return req
  })

  api.interceptors.response.use(
    (res) => res,
    async (err: AxiosError) => {
      const original = err.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined
      const status = err.response?.status
      const url = (original?.url || '').toString()
      const isRefreshCall = url.includes(routes['admin.auth.refresh'])

      if (status === 401 && isRefreshCall) return Promise.reject(err)

      if (status === 401 && original && !original._retry) {
        original._retry = true

        const sent = String(original.headers?.Authorization || '')
        const held = auth.accessToken ? `${auth.tokenType} ${auth.accessToken}` : ''

        if (held && sent !== held) {
          original.headers = original.headers || {}
          original.headers.Authorization = held
          return api(original)
        }

        const token = await auth.refreshTokens()

        if (token) {
          original.headers = original.headers || {}
          original.headers.Authorization = `${auth.tokenType} ${token}`
          return api(original)
        }

        if (!auth.refreshToken) await navigateTo(buildLoginLocation(nuxtApp.$router.currentRoute.value.fullPath))
      }

      return Promise.reject(err)
    },
  )

  return {
    provide: { axios: api },
  }
})
